import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import type { EnrichmentSelection } from './index';
import CellEnrichmentScheduleSummary, {
  enrichmentSelectionToCellSpecs
} from './CellEnrichmentScheduleSummary';

interface ConfirmCellEnrichmentDialogProps {
  open: boolean;
  selection: EnrichmentSelection | null;
  scheduleLabel: string;
  saving: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Defaults for new schedules; use "update" when editing an existing job's cells. */
  variant?: 'create' | 'update';
}

const ConfirmCellEnrichmentDialog: React.FC<ConfirmCellEnrichmentDialogProps> = ({
  open,
  selection,
  scheduleLabel,
  saving,
  onClose,
  onConfirm,
  variant = 'create',
}) => {
  const title =
    variant === 'update'
      ? 'Confirm enrichment cell update'
      : 'Confirm cell enrichment schedule';
  const confirmLabel = variant === 'update' ? 'Update schedule' : 'Confirm';
  const savingLabel =
    variant === 'update' ? 'Updating...' : 'Creating...';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {selection && (
          <CellEnrichmentScheduleSummary
            scheduleTypeLabel={scheduleLabel}
            cellSpecs={enrichmentSelectionToCellSpecs(selection)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
            backgroundColor: theme.customColors?.themeSecondary,
            color: theme.customColors?.themePrimary || theme.palette.primary.main,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:hover': {
              border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
              backgroundColor: theme.customColors?.themePrimary || theme.palette.primary.main,
              color: theme.customColors?.themeSecondary || 'white',
            },
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
            backgroundColor: theme.customColors?.themePrimary || theme.palette.primary.main,
            color: theme.customColors?.themeSecondary || 'white',
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor: theme.customColors?.themePrimary || theme.palette.primary.main,
              opacity: 0.9,
            },
          })}
        >
          {saving ? savingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmCellEnrichmentDialog;

